import { type FC } from 'react'
import { toast } from 'sonner'

import { CopyButtonWithAnimationActionIcon } from 'components/reusable/CopyButtonWithAnimation'
import { Badge } from 'slab'
import { type BrokersTask } from 'utils/web_api.schemas'

import { HoldBadge } from './badges/TypeBadges'
import {
  ExecutionContextBadge,
  ExecutionMethodBadge,
  TaskStatusBadge,
} from './WorkflowVisualIndicators'

type TaskCardHeaderProps = {
  task: BrokersTask
  showSequenceOrder?: boolean
}

const TaskCardHeader: FC<TaskCardHeaderProps> = ({ task, showSequenceOrder = true }) => {
  const handleCopyTaskId = (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      navigator.clipboard.writeText(task.id)
      toast.success('Task ID copied to clipboard', {
        position: 'top-center',
      })
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      toast.error('Failed to copy to clipboard', {
        position: 'top-center',
      })
    }
  }

  const formatTaskType = (type: string) => {
    return type
      .replace(/Task$/, '')
      .replace(/([A-Z])/g, ' $1')
      .trim()
  }

  return (
    <div className='flex justify-between gap-3 w-full'>
      {/* Left side - Task title and ID badge */}
      <div className='flex items-center gap-3'>
        <div>
          <h3 className='font-semibold text-lg text-gray-900'>
            {task.display_name || formatTaskType(task.task_type || '')}
          </h3>
        </div>
        <Badge
          type='neutral'
          bordered
          actionIcon={CopyButtonWithAnimationActionIcon}
          actionLabel='Copy Task ID'
          onActionClick={handleCopyTaskId as any}
        >
          {task.id.slice(-6)}
        </Badge>
      </div>

      {/* Right side - Status badges */}
      <div className='flex items-center gap-2'>
        <HoldBadge isOnHold={task.has_active_hold || false} />
        <TaskStatusBadge status={task.status} isExecutable={task.can_execute} />
        <ExecutionMethodBadge method={task.execution_method} />
        <ExecutionContextBadge context={task.execution_context} />
        {showSequenceOrder && task.sequence_order && (
          <span className='text-xs text-gray-400'>#{task.sequence_order}</span>
        )}
      </div>
    </div>
  )
}

export default TaskCardHeader
